import React from 'react';
import { createRoutesFromElements, generatePath, Navigate, Outlet, Route } from 'react-router';

import { CCMEntityTypes } from 'components/ccm/Dashboard/types';
import RouteErrorBoundary from 'components/error/RouteErrorBoundary';
import LazyLoader from 'components/ui/layout/LazyLoader';
import { CHECKLIST_ACCESSORY, CHECKLIST_PARTS, CHECKLIST_WORK } from 'constants/ChecklistConstants';
import { SALES, AFTER_SALES } from 'constants/ExtensionConstants';
import { BODYWORK_APPOINTMENT } from 'constants/FeatureConstants';
import { IS_PRODUCTION } from 'constants/Global';
import { PARTS } from 'constants/ModeConstants';
import { PARTS_REMINDER, REMINDER } from 'constants/ModuleConstants';
import { HOME_PAGES, PATH } from 'constants/RoutesConstants';
import { TECHNICIAN } from 'constants/UserConstants';

import { useRights } from 'hooks';

import { getCurrentRoute } from 'services/RouterUtils';
import { lazyRoute } from 'utils/router';

import App from './App';

const RouterLoader = () => <LazyLoader><Outlet /></LazyLoader>;

const HomeRedirect = () => {
  const { hasAccess } = useRights();

  const homePageUrl = React.useMemo(
    () => HOME_PAGES.find(definition => hasAccess(getCurrentRoute(definition.path))),
    [hasAccess]
  );

  return <Navigate to={generatePath((homePageUrl ?? PATH.PROFILE).path)} replace />;
};

export const routes = createRoutesFromElements(
  <Route Component={App}>
    <Route Component={RouterLoader} ErrorBoundary={RouteErrorBoundary}>
      <Route {...PATH.HOME} element={<HomeRedirect />} />

      <Route {...PATH.FOLLOW_UP} lazy={lazyRoute(() => import('components/interventions/FollowUp'))} />
      {/* After Sales */}
      <Route {...PATH.AFTERSALES} lazy={lazyRoute(() => import('components/interventions/dashboard/InterventionsDashboard'), { defaultMode: TECHNICIAN })} />
      <Route {...PATH.AFTERSALES_DETAILED} lazy={lazyRoute(() => import('components/interventions/detail/InterventionDetails'), { defaultMode: AFTER_SALES, originDashboard: PATH.AFTERSALES.path })} />
      <Route {...PATH.BODYWORKS} lazy={lazyRoute(() => import('components/interventions/dashboard/InterventionsDashboard'), { defaultMode: TECHNICIAN, defaultModule: BODYWORK_APPOINTMENT })} />
      <Route {...PATH.BODYWORKS_DETAILED} lazy={lazyRoute(() => import('components/interventions/detail/InterventionDetails'), { originDashboard: PATH.BODYWORKS.path })} />
      <Route {...PATH.REMINDERS} lazy={lazyRoute(() => import('components/reminders/dashboard/RemindersDashboard'), { module: REMINDER })} />
      <Route {...PATH.REMINDERS_DETAILED} lazy={lazyRoute(() => import('components/interventions/detail/InterventionDetails'), { originDashboard: PATH.REMINDERS.path })} />

      {/* Parts offers */}
      <Route {...PATH.PARTS} lazy={lazyRoute(() => import('components/interventions/dashboard/InterventionsDashboard'), { defaultMode: PARTS })} />
      <Route {...PATH.PARTS_DETAILED} lazy={lazyRoute(() => import('components/interventions/detail/InterventionDetails'), { originDashboard: PATH.PARTS.path })} />

      <Route {...PATH.PARTS_REMINDER} lazy={lazyRoute(() => import('components/reminders/dashboard/RemindersDashboard'), { module: PARTS_REMINDER })} />
      <Route {...PATH.PARTS_REMINDER_DETAILED} lazy={lazyRoute(() => import('components/interventions/detail/InterventionDetails'), { originDashboard: PATH.PARTS_REMINDER.path })} />

      {/* Sales */}
      <Route {...PATH.SALES} lazy={lazyRoute(() => import('components/interventions/dashboard/InterventionsDashboard'), { defaultMode: SALES })} />
      <Route {...PATH.SALES_DETAILED} lazy={lazyRoute(() => import('components/interventions/detail/InterventionDetails'), { originDashboard: PATH.SALES.path })} />
      <Route {...PATH.LEADS} lazy={lazyRoute(() => import('components/leads/Dashboard'))} />

      {/* Self-service */}
      <Route {...PATH.SELF_SERVICE_KANBAN} lazy={lazyRoute(() => import('components/interventions/Kanban'))} />
      <Route {...PATH.SELF_SERVICE_MOBILITY} lazy={lazyRoute(() => import('components/interventions/Mobility'))} />

      {/* Audit */}
      <Route {...PATH.AUDITS} lazy={lazyRoute(() => import('components/oditoo/audits/dashboard/AuditsDashboard'))} />
      <Route {...PATH.AUDITS_DETAILED} lazy={lazyRoute(() => import('components/oditoo/audits/details'))} />

      {/* Payments */}
      <Route {...PATH.PAYMENT} lazy={lazyRoute(() => import('components/payment/Dashboard'))} />

      {/* Dev-only component playground. Omitting the Route rather than gating inside the page means
          the path falls through to the 404 catch-all in production and acceptance. The paths are
          written here rather than added to `PATH`, because two E2E suites generate one case per
          entry of that object — `login/index.cy.js:80` performs a real Cognito login per path, and
          `readonly/index.cy.js` asserts read-only semantics. Neither is meaningful for a gallery. */}
      {!IS_PRODUCTION && (
        <>
          <Route path="/playground" lazy={lazyRoute(() => import('components/playground'))} />
          <Route path="/playground/:componentId" lazy={lazyRoute(() => import('components/playground'))} />
        </>
      )}

      {/* Customers */}
      <Route {...PATH.CUSTOMERS} lazy={lazyRoute(() => import('components/customers/dashboard/CustomersDashboard'))} />
      <Route {...PATH.CUSTOMERS_DETAILED} lazy={lazyRoute(() => import('components/customers/details/CustomerDetails'))} />

      {/* Chat */}
      <Route {...PATH.CHAT} lazy={lazyRoute(() => import('components/chat/dashboard'))} />
      <Route {...PATH.CHAT} path={PATH.CHAT.simplePath} lazy={lazyRoute(() => import('components/chat/dashboard'))} />

      {/* Statistics */}
      <Route {...PATH.STATS_AFTER_SALES} lazy={lazyRoute(() => import('components/statistics/afterSales/Dashboard'))} />
      <Route {...PATH.STATS_AFTER_SALES_DETAILS} lazy={lazyRoute(() => import('components/statistics/common/DetailsStatistics'), { mode: TECHNICIAN })} />
      <Route {...PATH.STATS_PARTS} lazy={lazyRoute(() => import('components/statistics/parts/Dashboard'))} />
      <Route {...PATH.STATS_PARTS_DETAILS} lazy={lazyRoute(() => import('components/statistics/common/DetailsStatistics'), { mode: PARTS })} />
      <Route {...PATH.STATS_SALES} lazy={lazyRoute(() => import('components/statistics/sales/Dashboard'))} />
      <Route {...PATH.STATS_SALES_DETAILS} lazy={lazyRoute(() => import('components/statistics/common/DetailsStatistics'), { mode: SALES })} />
      <Route path={PATH.STATS_ODITOO.simplePath}>
        <Route index lazy={lazyRoute(() => import('components/statistics/audit/dashboard/AuditsDashboardHeader'))} />
        <Route path=":dealerId" lazy={lazyRoute(() => import('components/statistics/audit/dashboard/AuditsDashboardHeader'))} />
      </Route>
      <Route {...PATH.SELF_SERVICE_STATS_DASHBOARD} lazy={lazyRoute(() => import('components/statistics/selfService/dashboard'))} />
      <Route {...PATH.SELF_SERVICE_STATS_DETAILS} lazy={lazyRoute(() => import('components/statistics/selfService/detail'))} />

      <Route {...PATH.BILLING} lazy={lazyRoute(() => import('components/metrics'))} />

      {/* Manufacturer statistics */}
      <Route {...PATH.MANUFACTURER_STATS_AFTER_SALES} lazy={lazyRoute(() => import('components/statistics/afterSales/Dashboard'))} />
      <Route {...PATH.MANUFACTURER_STATS_AFTER_SALES_DETAILS} lazy={lazyRoute(() => import('components/statistics/common/DetailsStatistics'), { mode: TECHNICIAN })} />
      <Route {...PATH.MANUFACTURER_STATS_SELF_SERVICE} lazy={lazyRoute(() => import('components/statistics/selfService/dashboard'))} />
      <Route {...PATH.MANUFACTURER_STATS_SELF_SERVICE_DETAILS} lazy={lazyRoute(() => import('components/statistics/selfService/detail'))} />
      <Route {...PATH.MANUFACTURER_STATS_PARTS} lazy={lazyRoute(() => import('components/statistics/parts/Dashboard'))} />
      <Route {...PATH.MANUFACTURER_STATS_PARTS_DETAILS} lazy={lazyRoute(() => import('components/statistics/common/DetailsStatistics'), { mode: PARTS })} />
      <Route {...PATH.MANUFACTURER_STATS_SALES} lazy={lazyRoute(() => import('components/statistics/sales/Dashboard'))} />
      <Route {...PATH.MANUFACTURER_STATS_SALES_DETAILS} lazy={lazyRoute(() => import('components/statistics/common/DetailsStatistics'), { mode: SALES })} />

      {/* Configurator */}
      <Route {...PATH.CONFIGURATOR_OFFERS} lazy={lazyRoute(() => import('components/configurator/offers/ConfiguratorOffersDashboard'))} />
      <Route {...PATH.CONFIGURATOR_OFFERS_DETAILED} lazy={lazyRoute(() => import('components/configurator/new-offer/ConfiguratorNewOffer'))} />

      {/* Appointments - Keydeposit */}
      <Route {...PATH.KEY_DROP_OFF_LOCKER_PLANNING} lazy={lazyRoute(() => import('components/keydeposit/Calendar'))} />
      <Route {...PATH.KEY_DROP_OFF_LOCKER_DETAILED} lazy={lazyRoute(() => import('components/keydeposit/lockers/Details'))} />
      <Route {...PATH.KEY_DROP_OFF_APPOINTMENTS} lazy={lazyRoute(() => import('components/keydeposit/appointments/dashboard'))} />
      <Route {...PATH.KEY_DROP_OFF_APPOINTMENTS_DETAILED} lazy={lazyRoute(() => import('components/keydeposit/appointments/details'))} />

      {/* Notifications */}
      <Route {...PATH.USER_NOTIFICATIONS_SETTINGS} lazy={lazyRoute(() => import('components/configuration/user/notifications/settings'))} />
      <Route {...PATH.USER_NOTIFICATIONS} lazy={lazyRoute(() => import('components/notifications/Dashboard'))} />

      {/* Administration */}
      <Route {...PATH.DEALERS} lazy={lazyRoute(() => import('components/configuration/administration/dealers/DealersDashboard'))} />
      <Route {...PATH.DEALERS_DETAILS} lazy={lazyRoute(() => import('components/configuration/administration/dealers/Details'))} />

      {/* Group management */}
      <Route {...PATH.GROUP_CONFIG_PAGE} lazy={lazyRoute(() => import('components/configuration/group/Global'))} />
      <Route {...PATH.GROUP_MANAGEMENT}>
        <Route index lazy={lazyRoute(() => import('components/configuration/group'))} />

        <Route path="ccm/:tabKey?" handle={PATH.GROUP_CCM.handle} lazy={lazyRoute(() => import('components/ccm/Dashboard'), { entityType: CCMEntityTypes.groups })} />

        <Route path="devices" handle={PATH.GROUP_DEVICES.handle} lazy={lazyRoute(() => import('components/configuration/dealer/devices/dashboard/DevicesDashboard'))} />
        <Route path="media-library" handle={PATH.MEDIA_LIBRARY_GROUP.handle} lazy={lazyRoute(() => import('components/configuration/group/components/content/mediaLibrary'))} />
        <Route path="publicTokens" handle={PATH.GROUP_PUBLIC_TOKENS_DASHBOARD.handle} lazy={lazyRoute(() => import('components/publicTokens/dashboard'), { group: true })} />
        <Route path="sms-accounts" handle={PATH.GROUP_SMS_ACCOUNTS_DASHBOARD.handle} lazy={lazyRoute(() => import('components/smsAccounts/dashboard'), { group: true })} />
        <Route path="templates" handle={PATH.GROUP_TEMPLATES_CONFIG.handle} lazy={lazyRoute(() => import('components/configuration/group/components/content/templates'))} />

        <Route path="guidelines" handle={PATH.GROUP_GUIDELINES_CONFIG.handle}>
          <Route index lazy={lazyRoute(() => import('components/configuration/group/components/content/Guidelines'))} />
          <Route path=":guidelineId" handle={PATH.GROUP_GUIDELINE_DETAILED.handle} lazy={lazyRoute(() => import('components/configuration/guidelines/Details'))} />
        </Route>

        <Route path="checklists" handle={PATH.GROUP_CHECKLISTS_CONFIG.handle}>
          <Route index lazy={lazyRoute(() => import('components/configuration/dealer/checklists/dashboard/ChecklistsDashboard'), { isGroup: true, type: CHECKLIST_WORK })} />
          <Route
            path=":checklistId"
            handle={PATH.GROUP_CHECKLISTS_DETAILED.handle}
            lazy={lazyRoute(() => import('components/configuration/dealer/checklists/detail'), { type: CHECKLIST_WORK })}
          />
        </Route>

        <Route path="parts-offers" handle={PATH.GROUP_PARTS_OFFERS.handle}>
          <Route index lazy={lazyRoute(() => import('components/configuration/dealer/checklists/dashboard/ChecklistsDashboard'), { isGroup: true, type: CHECKLIST_PARTS })} />
          <Route
            path=":checklistId"
            handle={PATH.GROUP_PARTS_OFFERS_DETAILS.handle}
            lazy={lazyRoute(() => import('components/configuration/dealer/checklists/detail'), { type: CHECKLIST_PARTS })}
          />
        </Route>

        <Route path="sales-offers" handle={PATH.GROUP_OFFERS.handle}>
          <Route index lazy={lazyRoute(() => import('components/configuration/dealer/checklists/dashboard/ChecklistsDashboard'), { isGroup: true, type: CHECKLIST_ACCESSORY })} />
          <Route
            path=":checklistId"
            handle={PATH.GROUP_OFFERS_DETAILED.handle}
            lazy={lazyRoute(() => import('components/configuration/dealer/checklists/detail'), { type: CHECKLIST_ACCESSORY })}
          />
        </Route>

        <Route path="extras" handle={PATH.GROUP_EXTRA_DASHBOARD.handle}>
          <Route index lazy={lazyRoute(() => import('components/configuration/dealer/SelfService/Dashboard/EntityDashboard'), { group: true, entity: 'extras' })} />
          <Route path=":checklistId" handle={PATH.GROUP_EXTRA_DETAILED.handle} lazy={lazyRoute(() => import('components/configuration/dealer/SelfService/Extra'))} />
        </Route>

        <Route path="questionModels" handle={PATH.GROUP_QUESTION_MODEL_DASHBOARD.handle}>
          <Route index lazy={lazyRoute(() => import('components/configuration/dealer/SelfService/Dashboard/EntityDashboard'), { group: true, entity: 'questions' })} />
          <Route
            path=":questionModelId"
            handle={PATH.GROUP_QUESTION_MODEL_DETAILED.handle}
            lazy={lazyRoute(() => import('components/configuration/dealer/SelfService/QuestionModel'))}
          />
        </Route>

        <Route path="vehicleCheckModel" handle={PATH.GROUP_VEHICLE_CHECK_MODEL_DASHBOARD.handle}>
          <Route index lazy={lazyRoute(() => import('components/configuration/dealer/SelfService/Dashboard/EntityDashboard'), { group: true, entity: 'vehiclesCheck' })} />
          <Route
            path=":vehicleCheckModelId"
            handle={PATH.GROUP_VEHICLE_CHECK_MODEL_DETAILED.handle}
            lazy={lazyRoute(() => import('components/configuration/dealer/SelfService/VehicleCheckModel'))}
          />
        </Route>

        <Route path="payers" handle={PATH.GROUP_PAYERS.handle}>
          <Route index lazy={lazyRoute(() => import('components/configuration/payers/Dashboard'), { group: true })} />
          <Route path=":payerId" handle={PATH.GROUP_PAYERS_DETAILS.handle} lazy={lazyRoute(() => import('components/configuration/payers/Details'))} />
        </Route>

        <Route path="screensavers" handle={PATH.GROUP_CONFIG_SCREENSAVERS.handle}>
          <Route index lazy={lazyRoute(() => import('components/kiosk/screensavers/Dashboard'), { group: true })} />
          <Route
            path=":screensaverId"
            handle={PATH.GROUP_CONFIG_SCREENSAVER_DETAILS.handle}
            lazy={lazyRoute(() => import('components/kiosk/screensavers/Details'))}
          />
        </Route>

        <Route path="tags/:mode" handle={PATH.GROUP_TAGS_DASHBOARD.handle}>
          <Route index lazy={lazyRoute(() => import('components/tags/group-dashboard'))} />
          <Route path=":tagId" handle={PATH.GROUP_TAGS_DETAILS.handle} lazy={lazyRoute(() => import('components/tags/details'), { group: true })} />
        </Route>
      </Route>

      {/* Dealership management */}
      <Route {...PATH.DEALER_MANAGEMENT}>
        <Route index lazy={lazyRoute(() => import('components/configuration/management'))} />

        <Route path="ccm/:tabKey?" handle={PATH.DEALER_CCM.handle} lazy={lazyRoute(() => import('components/ccm/Dashboard'), { entityType: CCMEntityTypes.dealers })} />

        <Route path="user-groups" handle={PATH.USER_GROUPS.handle} lazy={lazyRoute(() => import('components/configuration/dealer/userGroups'))} />
        <Route path="devices" handle={PATH.DEVICES.handle} lazy={lazyRoute(() => import('components/configuration/dealer/devices/dashboard/DevicesDashboard'))} />
        <Route path="whitelist" handle={PATH.WHITELIST.handle} lazy={lazyRoute(() => import('components/configuration/dealer/whitelist/Dashboard'))} />
        <Route path="hardwares" handle={PATH.HARDWARES.handle} lazy={lazyRoute(() => import('components/hardware/dashboard'))} />
        <Route path="safes" handle={PATH.SAFE_CONFIG.handle} lazy={lazyRoute(() => import('components/configuration/dealer/Safe'))} />
        <Route path="media-library" handle={PATH.MEDIA_LIBRARY_DEALER.handle} lazy={lazyRoute(() => import('components/configuration/dealer/mediaLibrary'))} />
        <Route path="publicTokens" handle={PATH.DEALER_PUBLIC_TOKENS_DASHBOARD.handle} lazy={lazyRoute(() => import('components/publicTokens/dashboard'))} />
        <Route path="sms-accounts" handle={PATH.DEALER_SMS_ACCOUNTS_DASHBOARD.handle} lazy={lazyRoute(() => import('components/smsAccounts/dashboard'))} />
        <Route path="models" handle={PATH.CONFIGURATOR_MODELS.handle} lazy={lazyRoute(() => import('components/configurator/models/dashboard/ModelsDashboard'))} />

        <Route path="users" handle={PATH.USERS.handle}>
          <Route index lazy={lazyRoute(() => import('components/configuration/dealer/users/dashboard/UsersDashboard'))} />
          <Route path=":userId" handle={PATH.USERS_DETAILED.handle} lazy={lazyRoute(() => import('components/configuration/dealer/users/detail'))} />
        </Route>

        <Route path="payers" handle={PATH.PAYERS.handle}>
          <Route index lazy={lazyRoute(() => import('components/configuration/payers/Dashboard'))} />
          <Route path=":payerId" handle={PATH.PAYERS_DETAILS.handle} lazy={lazyRoute(() => import('components/configuration/payers/Details'))} />
        </Route>

        {/* Kiosk */}
        <Route path="kiosks" handle={PATH.KIOSKS.handle}>
          <Route index lazy={lazyRoute(() => import('components/kiosk/Dashboard'))} />
          <Route path=":kioskId" handle={PATH.KIOSKS_DETAILS.handle} lazy={lazyRoute(() => import('components/kiosk/Details'))} />
        </Route>

        {/* Camera */}
        <Route path="cameras" handle={PATH.CAMERAS.handle}>
          <Route index lazy={lazyRoute(() => import('components/camera/Dashboard'))} />
          <Route path=":cameraId" handle={PATH.CAMERAS_DETAILS.handle} lazy={lazyRoute(() => import('components/camera/Details'))} />
        </Route>

        <Route path="screensavers" handle={PATH.DEALER_CONFIG_SCREENSAVERS.handle}>
          <Route index lazy={lazyRoute(() => import('components/kiosk/screensavers/Dashboard'))} />
          <Route
            path=":screensaverId"
            handle={PATH.DEALER_CONFIG_SCREENSAVER_DETAILS.handle}
            lazy={lazyRoute(() => import('components/kiosk/screensavers/Details'))}
          />
        </Route>

        <Route path="audit-models" handle={PATH.AUDIT_MODELS.handle}>
          <Route index lazy={lazyRoute(() => import('components/oditoo/auditModels/dashboard'))} />
          <Route path=":auditModelId" handle={PATH.AUDIT_MODELS_DETAILED.handle} lazy={lazyRoute(() => import('components/oditoo/auditModels/details'))} />
        </Route>

        <Route path="checklists" handle={PATH.CHECKLISTS.handle}>
          <Route index lazy={lazyRoute(() => import('components/configuration/dealer/checklists/dashboard/ChecklistsDashboard'), { type: CHECKLIST_WORK })} />
          <Route
            path=":checklistId"
            handle={PATH.CHECKLISTS_DETAILED.handle}
            lazy={lazyRoute(() => import('components/configuration/dealer/checklists/detail'), { type: CHECKLIST_WORK })}
          />
        </Route>

        <Route path="guidelines" handle={PATH.GUIDELINES_DASHBOARD.handle}>
          <Route index lazy={lazyRoute(() => import('components/configuration/guidelines/Dashboard'))} />
          <Route path=":guidelineId" handle={PATH.GUIDELINE_DETAILED.handle} lazy={lazyRoute(() => import('components/configuration/guidelines/Details'))} />
        </Route>

        <Route path="predefined-messages" handle={PATH.PREDEFINED_MESSAGES_DASHBOARD.handle}>
          <Route index lazy={lazyRoute(() => import('components/configuration/predefinedMessages/Dashboard'))} />
          <Route
            path=":predefinedMessageId"
            handle={PATH.PREDEFINED_MESSAGES_DETAILED.handle}
            lazy={lazyRoute(() => import('components/configuration/predefinedMessages/Details'))}
          />
        </Route>

        <Route path="sales-offers" handle={PATH.OFFERS_DASHBOARD.handle}>
          <Route
            index
            lazy={lazyRoute(() => import('components/configuration/dealer/checklists/dashboard/ChecklistsDashboard'), { dealerButton: { params: { extensions: [SALES] } }, type: CHECKLIST_ACCESSORY })}
          />
          <Route
            path=":checklistId"
            handle={PATH.OFFERS_DETAILED.handle}
            lazy={lazyRoute(() => import('components/configuration/dealer/checklists/detail'), { type: CHECKLIST_ACCESSORY })}
          />
        </Route>

        <Route path="parts-offers" handle={PATH.DEALER_PARTS_OFFERS.handle}>
          <Route
            index
            lazy={lazyRoute(() => import('components/configuration/dealer/checklists/dashboard/ChecklistsDashboard'), { dealerButton: { params: { extensions: [PARTS] } }, type: CHECKLIST_PARTS })}
          />
          <Route
            path=":checklistId"
            handle={PATH.PARTS_OFFERS_DETAILS.handle}
            lazy={lazyRoute(() => import('components/configuration/dealer/checklists/detail'), { type: CHECKLIST_PARTS })}
          />
        </Route>

        <Route path="extras" handle={PATH.DEALER_EXTRA_DASHBOARD.handle}>
          <Route index lazy={lazyRoute(() => import('components/configuration/dealer/SelfService/Dashboard/EntityDashboard'), { entity: 'extras' })} />
          <Route path=":checklistId" handle={PATH.DEALER_EXTRA_DETAILED.handle} lazy={lazyRoute(() => import('components/configuration/dealer/SelfService/Extra'))} />
        </Route>

        <Route path="questionModels" handle={PATH.DEALER_QUESTION_MODEL_DASHBOARD.handle}>
          <Route index lazy={lazyRoute(() => import('components/configuration/dealer/SelfService/Dashboard/EntityDashboard'), { entity: 'questions' })} />
          <Route
            path=":questionModelId"
            handle={PATH.QUESTION_MODEL_DETAILED.handle}
            lazy={lazyRoute(() => import('components/configuration/dealer/SelfService/QuestionModel'))}
          />
        </Route>

        <Route path="vehicleCheckModel" handle={PATH.DEALER_VEHICLE_CHECK_MODEL_DASHBOARD.handle}>
          <Route index lazy={lazyRoute(() => import('components/configuration/dealer/SelfService/Dashboard/EntityDashboard'), { entity: 'vehiclesCheck' })} />
          <Route
            path=":vehicleCheckModelId"
            handle={PATH.VEHICLE_CHECK_MODEL_DETAILED.handle}
            lazy={lazyRoute(() => import('components/configuration/dealer/SelfService/VehicleCheckModel'))}
          />
        </Route>

        <Route path="steps/:mode" handle={PATH.STEPS_DASHBOARD.handle}>
          <Route index lazy={lazyRoute(() => import('components/configuration/dealer/Steps/Dashboard'))} />
          <Route path=":stepId" handle={PATH.STEPS_DETAILS.handle} lazy={lazyRoute(() => import('components/configuration/dealer/Steps/Details'))} />
        </Route>

        <Route path="tags/:mode" handle={PATH.DEALER_TAGS_DASHBOARD.handle}>
          <Route index lazy={lazyRoute(() => import('components/tags/dashboard'))} />
          <Route path=":tagId" handle={PATH.DEALER_TAGS_DETAILS.handle} lazy={lazyRoute(() => import('components/tags/details'), { group: false })} />
        </Route>

        <Route path="gates" handle={PATH.GATE_MANAGEMENT_DASHBOARD.handle}>
          <Route index lazy={lazyRoute(() => import('components/configuration/gates/Dashboard'))} />
          <Route path=":gateId" handle={PATH.GATE_MANAGEMENT_DETAILS.handle} lazy={lazyRoute(() => import('components/configuration/gates/Details'))} />
        </Route>
      </Route>

      {/* Personal management */}
      <Route {...PATH.PERSONAL_CONFIG} lazy={lazyRoute(() => import('components/configuration/personal'))} />
      <Route {...PATH.PROFILE} lazy={lazyRoute(() => import('components/configuration/dealer/users/detail/components/ProfileDetail'))} />
      <Route {...PATH.MEDIA_LIBRARY_PERSONAL} lazy={lazyRoute(() => import('components/configuration/user/mediaLibrary'))} />

      {/* Others */}
      <Route {...PATH.ERROR_GLOBAL} lazy={lazyRoute(() => import('components/common/PageError'))} />
      <Route {...PATH.ERROR_404} lazy={lazyRoute(() => import('components/common/Page404'))} />

      {/* Dealer config v3 */}
      <Route {...PATH.DEALER_CONFIG_PAGE} lazy={lazyRoute(() => import('components/configuration/dealer/Global'))} />

      {/* Campaign */}
      <Route {...PATH.CAMPAIGNS_DASHBOARD} lazy={lazyRoute(() => import('components/campaigns/Dashboard'))} />
      <Route {...PATH.CAMPAIGNS_DETAILS} lazy={lazyRoute(() => import('components/campaigns/Details'))} />
      <Route {...PATH.CONTACT_LISTS_DASHBOARD} lazy={lazyRoute(() => import('components/contactLists/Dashboard'))} />
      <Route {...PATH.CONTACT_LISTS_DETAILS} lazy={lazyRoute(() => import('components/contactLists/Details'))} />

      {/* Release Notes */}
      <Route {...PATH.RELEASES_DASHBOARD} lazy={lazyRoute(() => import('components/releases/Dashboard'))} />
      <Route {...PATH.RELEASES_DETAILS} lazy={lazyRoute(() => import('components/releases/Details'))} />

      <Route path="*" lazy={lazyRoute(() => import('components/common/Page404'))} />
    </Route>
  </Route>
);
